import { tokenUtils } from '../utils/tokenUtils';

const API_BASE = process.env.REACT_APP_API_URL;
const API_URL_DOCUMENT = `${API_BASE}/Document/upload`;
const API_URL_VIDEO = `${API_BASE}/Video/upload`;

const getAuthToken = () => tokenUtils.get();

const getAuthHeaders = (isJson = true) => {

  const token = getAuthToken();
  if (!token) throw new Error('No se encontró token de autenticación');
  
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (isJson) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const fetchVideos = async () => {
  try {
    const res = await fetch(`${API_BASE}/Video`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Error al obtener videos');
    const data = await res.json();
    return data.map((item, index) => ({
      ...item,
      tipo: 'video',
      id: item.id ?? `video-${index}`,
      labels: normalizeTags(item.labels)
    }));
  } catch (error) {
    console.error('Error en fetchVideos:', error);
    throw error;
  }
};

const mapDocumentTipo = (item) => {
  const contentType = (item.contentType || item.ContentType || 'document').toLowerCase();
  if (contentType === 'audio' || contentType === 'podcast') return 'audio';
  if (contentType === 'link' || contentType === 'enlace') return 'link';
  return 'documento';
};

const fetchDocuments = async () => {
  try {
    const res = await fetch(`${API_BASE}/Document`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Error al obtener documentos');
    const data = await res.json();
    return data.map((item, index) => ({
      ...item,
      tipo: mapDocumentTipo(item),
      contentType: (item.contentType || item.ContentType || 'document').toLowerCase(),
      thumbnailPath: item.thumbnailPath || item.ThumbnailPath || null,
      externalUrl: item.externalUrl || item.ExternalUrl || null,
      id: item.id ?? `doc-${index}`,
      labels: normalizeTags(item.labels)
    }));
  } catch (error) {
    console.error('Error en fetchDocuments:', error);
    throw error;
  }
};


const normalizeTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') return tags.split(',').map(t => t.trim());
  return [];
};


const uploadArchivo = async (formData, tipoArchivo) => {
  const url = tipoArchivo === 'Video' ? API_URL_VIDEO : API_URL_DOCUMENT;
  // PDF / Audio / Link usan Document/upload

  try {
    const token = getAuthToken();
    if (!token) throw new Error('No se encontró token de autenticación');

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let message = 'Error al subir el archivo';
      try {
        const errorData = JSON.parse(text);
        console.error('Error al subir el archivo:', errorData);
        message =
          errorData.message ||
          errorData.title ||
          (typeof errorData === 'string' ? errorData : message);
      } catch {
        if (text) message = text;
      }
      throw new Error(message);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en uploadArchivo:', error);
    throw error;
  }
};

const deleteVideo = async (id) => {
  const res = await fetch(`${API_BASE}/Video/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al eliminar el video');
  return await res;
}

const deleteDocument = async (id) => {
  const res = await fetch(`${API_BASE}/Document/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Error al eliminar el documento');
  return await res;
}

const bulkUploadDocuments = async (payload, onUploadProgress) => {
  const token = getAuthToken();
  if (!token) throw new Error('No se encontró token de autenticación');

  const formData = new FormData();
  (payload.files || []).forEach((file) => {
    formData.append('Files', file, file.name);
  });

  if (payload.uploadedBy) formData.append('UploadedBy', String(payload.uploadedBy));
  if (payload.contentType) formData.append('ContentType', payload.contentType);
  if (payload.category) formData.append('Category', payload.category);
  if (payload.labels) formData.append('Labels', payload.labels);
  if (payload.description) formData.append('Description', payload.description);
  if (payload.isValid !== undefined && payload.isValid !== null) {
    formData.append('IsValid', String(Boolean(payload.isValid)));
  }

  // Usar XMLHttpRequest para progreso de subida
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/Document/bulk-upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (typeof onUploadProgress === 'function') {
        onUploadProgress(event);
      }
    };

    xhr.onload = () => {
      const text = xhr.responseText || '';
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(text ? JSON.parse(text) : {});
        } catch {
          resolve({});
        }
        return;
      }

      let message = 'Error en la carga masiva de documentos';
      try {
        const data = JSON.parse(text);
        message = data.message || data.title || (typeof data === 'string' ? data : message);
      } catch {
        if (text) message = text;
      }
      reject(new Error(message));
    };

    xhr.onerror = () => reject(new Error('Error de red al subir documentos'));
    xhr.send(formData);
  });
};

const RepositoryService = {
  fetchVideos,
  fetchDocuments,
  uploadArchivo,
  bulkUploadDocuments,
  deleteVideo,
  deleteDocument,
};

export default RepositoryService;
